import { esc } from '../../utils/format';
import React, { useState, useMemo } from 'react';
import {
  calcularCuotaMensual,
  generarTablaAmortizacion,
  calcularCuotaConInteresCapital,
  generarTablaConInteresCapital,
  generarTablaBono,
  generarTablaBonoCapital,
  FRECUENCIAS_BONO,
} from '../modulos/prestamos/utils/prestamosHelper';
import { Calculator, FileText, Printer, ChevronDown, ChevronUp, ArrowLeft, CreditCard, Landmark } from 'lucide-react';

/* ─── helpers ─── */
const fmtCLP   = (n) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n || 0);
const fmtFecha = (d) => new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
const hoy      = ()  => new Date().toISOString().split('T')[0];

/* ════════════════════════════════════════════════════════════ */
export default function CalculadoraCredito() {

  /* ── MODO ── */
  const [modo, setModo] = useState('credito'); // 'credito' | 'bono'

  /* ── params crédito ── */
  const [monto,          setMonto]          = useState('');
  const [tasaAnual,      setTasaAnual]      = useState('');
  const [plazo,          setPlazo]          = useState('');
  const [fechaInicio,    setFechaInicio]    = useState(hoy());
  const [diaPago,        setDiaPago]        = useState('');
  const [tipoInteres,    setTipoInteres]    = useState('frances');
  const [interesCapital, setInteresCapital] = useState('');

  /* ── params bono ── */
  const [frecuencia,     setFrecuencia]     = useState('mensual');
  const [tipoBono,       setTipoBono]       = useState('tasa');  // 'tasa' | 'monto_fijo'
  const [bInteresTotal,  setBInteresTotal]  = useState('');

  /* ── propuesta ── */
  const [empresa,        setEmpresa]        = useState('');
  const [rutEmpresa,     setRutEmpresa]     = useState('');
  const [prestamista,    setPrestamista]    = useState('');
  const [rutPrestamista, setRutPrestamista] = useState('');
  const [proposito,      setProposito]      = useState('');
  const [garantia,       setGarantia]       = useState('');
  const [notas,          setNotas]          = useState('');

  /* ── UI ── */
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [mostrarForm,  setMostrarForm]  = useState(false);

  /* ─── cálculos crédito ─── */
  const resultadoCredito = useMemo(() => {
    if (modo !== 'credito') return null;
    const m  = parseInt(String(monto).replace(/\./g, '')) || 0;
    const t  = parseFloat(String(tasaAnual).replace(',', '.')) || 0;
    const p  = parseInt(plazo) || 0;
    const ic = parseInt(String(interesCapital).replace(/\./g, '')) || 0;
    if (m <= 0 || p <= 0) return null;
    const fecha = new Date(fechaInicio + 'T12:00:00');
    const dia   = parseInt(diaPago) || null;
    if (tipoInteres === 'capital_fijo') {
      const cuotaMensual = calcularCuotaConInteresCapital(m, ic, p);
      const cuotas       = generarTablaConInteresCapital(m, ic, p, fecha, dia);
      return { cuotaMensual, cuotas, totalPagar: cuotaMensual * p, totalIntereses: ic, monto: m, plazo: p };
    }
    const cuotaMensual = calcularCuotaMensual(m, t, p);
    const cuotas       = generarTablaAmortizacion(m, t, p, fecha, dia);
    const totalPagar   = cuotaMensual * p;
    return { cuotaMensual, cuotas, totalPagar, totalIntereses: totalPagar - m, monto: m, plazo: p };
  }, [modo, monto, tasaAnual, plazo, fechaInicio, diaPago, tipoInteres, interesCapital]);

  /* ─── cálculos bono ─── */
  const resultadoBono = useMemo(() => {
    if (modo !== 'bono') return null;
    const m  = parseInt(String(monto).replace(/\./g, '')) || 0;
    const t  = parseFloat(String(tasaAnual).replace(',', '.')) || 0;
    const p  = parseInt(plazo) || 0;
    const it = parseInt(String(bInteresTotal).replace(/\./g, '')) || 0;
    if (m <= 0 || p <= 0) return null;
    const fecha = new Date(fechaInicio + 'T12:00:00');
    const dia   = parseInt(diaPago) || null;
    const freq  = FRECUENCIAS_BONO[frecuencia];
    const numPeriodos = Math.round(p / freq.mesesPorPeriodo);

    let cuotas, cupon, totalIntereses;
    if (tipoBono === 'monto_fijo') {
      cuotas         = generarTablaBonoCapital(m, it, p, fecha, frecuencia, dia);
      cupon          = Math.round(it / numPeriodos);
      totalIntereses = it;
    } else {
      cuotas         = generarTablaBono(m, t, p, fecha, frecuencia, dia);
      cupon          = cuotas[0]?.intereses || 0;
      totalIntereses = cupon * numPeriodos;
    }
    const pagoFinal   = cupon + m; // último período: cupón + devolución capital
    const totalPagar  = totalIntereses + m;
    return { cupon, cuotas, totalIntereses, totalPagar, pagoFinal, monto: m, plazo: p, numPeriodos, frecuencia };
  }, [modo, monto, tasaAnual, plazo, fechaInicio, diaPago, frecuencia, tipoBono, bInteresTotal]);

  const resultado = modo === 'credito' ? resultadoCredito : resultadoBono;

  /* ─── print template ─── */
  const buildPrintHtml = () => {
    if (!resultado) return '';
    const esCredito = modo === 'credito';
    const freqLabel = FRECUENCIAS_BONO[frecuencia]?.label || '';

    const filas = resultado.cuotas.map((c, i) => {
      const saldo = esCredito
        ? resultado.monto - resultado.cuotas.slice(0, i + 1).reduce((s, x) => s + x.capital, 0)
        : c.saldo;
      const claseUltima = !esCredito && c.esFinal ? 'background:#faf5ff;font-weight:700' : '';
      return `<tr style="${claseUltima}">
        <td style="text-align:center;padding:7px 10px;border-bottom:1px solid #f1f5f9;font-weight:600;color:#64748b">${c.numero}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f1f5f9">${fmtFecha(c.fechaVencimiento)}</td>
        <td style="text-align:right;padding:7px 10px;border-bottom:1px solid #f1f5f9">${fmtCLP(c.monto)}</td>
        <td style="text-align:right;padding:7px 10px;border-bottom:1px solid #f1f5f9;color:#15803d">${fmtCLP(c.capital)}</td>
        <td style="text-align:right;padding:7px 10px;border-bottom:1px solid #f1f5f9;color:#c2410c">${fmtCLP(c.intereses)}</td>
        <td style="text-align:right;padding:7px 10px;border-bottom:1px solid #f1f5f9;color:#64748b">${fmtCLP(Math.max(0, Math.round(saldo)))}</td>
      </tr>`;
    }).join('');

    const color = esCredito ? '#7c3aed' : '#0369a1';
    const colorLight = esCredito ? '#ddd6fe' : '#bae6fd';
    const titulo = esCredito ? 'PROPUESTA DE CRÉDITO' : 'PROPUESTA DE BONO';
    const cuotaLabel = esCredito ? 'Cuota mensual' : `Cupón ${freqLabel.toLowerCase()}`;

    return `<!DOCTYPE html><html lang="es"><head>
      <meta charset="UTF-8"/><title>${esc(titulo)}${empresa ? ' — ' + esc(empresa) : ''}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1e293b;background:#fff;padding:40px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        thead th{background:${color};color:#fff;padding:8px 10px;text-align:right;font-weight:700}
        thead th:first-child{text-align:center}
        tbody tr:nth-child(even){background:#fafafa}
        tfoot td{padding:9px 10px;font-weight:800;background:#f8fafc;border-top:2px solid #e2e8f0;text-align:right}
        tfoot td:first-child{text-align:left}
      </style></head><body>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${color};padding-bottom:20px;margin-bottom:28px">
        <div><h1 style="font-size:22px;font-weight:800;color:${color}">${empresa || 'Empresa Solicitante'}</h1>
          <p style="font-size:11px;color:#64748b;margin-top:2px">${rutEmpresa ? 'RUT: ' + rutEmpresa : ''}</p></div>
        <div style="text-align:right;font-size:11px;color:#64748b">
          <strong style="display:block;font-size:15px;color:#1e293b;font-weight:700">${titulo}</strong>
          ${new Date().toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
      </div>

      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:${color};border-bottom:1px solid ${colorLight};padding-bottom:6px;margin:0 0 14px">Partes</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px 28px;margin-bottom:20px">
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Solicitante (Deudor)</span><p style="font-size:13px;font-weight:600;margin-top:2px">${empresa || '—'}</p>${rutEmpresa ? `<span style="font-size:11px;color:#64748b">RUT: ${rutEmpresa}</span>` : ''}</div>
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">${esCredito ? 'Prestamista (Acreedor)' : 'Inversionista (Acreedor)'}</span><p style="font-size:13px;font-weight:600;margin-top:2px">${prestamista || '—'}</p>${rutPrestamista ? `<span style="font-size:11px;color:#64748b">RUT: ${rutPrestamista}</span>` : ''}</div>
      </div>

      ${proposito ? `<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:${color};border-bottom:1px solid ${colorLight};padding-bottom:6px;margin:0 0 10px">Propósito</div><p style="font-size:13px;color:#334155;margin-bottom:20px">${proposito}</p>` : ''}

      <div style="background:#f5f3ff;border:1px solid ${colorLight};border-radius:10px;padding:16px 20px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:24px">
        <div><span style="font-size:10px;color:#5b21b6;text-transform:uppercase">Monto</span><p style="font-size:20px;font-weight:800;color:${color};margin-top:2px">${fmtCLP(resultado.monto)}</p></div>
        <div><span style="font-size:10px;color:#5b21b6;text-transform:uppercase">${cuotaLabel}</span><p style="font-size:20px;font-weight:800;color:#15803d;margin-top:2px">${fmtCLP(Math.round(esCredito ? resultado.cuotaMensual : resultado.cupon))}</p></div>
        <div><span style="font-size:10px;color:#5b21b6;text-transform:uppercase">${esCredito ? 'Total a devolver' : 'Pago final (capital+cupón)'}</span><p style="font-size:20px;font-weight:800;color:#1d4ed8;margin-top:2px">${fmtCLP(Math.round(esCredito ? resultado.totalPagar : resultado.pagoFinal))}</p></div>
      </div>

      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:${color};border-bottom:1px solid ${colorLight};padding-bottom:6px;margin:0 0 14px">Condiciones</div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px 20px;margin-bottom:24px;font-size:12px">
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Monto</span><p style="font-weight:600;margin-top:2px">${fmtCLP(resultado.monto)}</p></div>
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Plazo</span><p style="font-weight:600;margin-top:2px">${resultado.plazo} meses</p></div>
        ${esCredito
          ? `<div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Tipo</span><p style="font-weight:600;margin-top:2px">${tipoInteres === 'frances' ? 'Tasa anual ' + tasaAnual + '%' : 'Interés fijo ' + fmtCLP(parseInt(interesCapital) || 0)}</p></div>`
          : `<div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Freq. cupón</span><p style="font-weight:600;margin-top:2px">${freqLabel}</p></div>`
        }
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Total intereses</span><p style="font-weight:600;margin-top:2px;color:#c2410c">${fmtCLP(Math.round(resultado.totalIntereses))}</p></div>
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Primer pago</span><p style="font-weight:600;margin-top:2px">${fmtFecha(resultado.cuotas[0]?.fechaVencimiento)}</p></div>
        <div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Último pago</span><p style="font-weight:600;margin-top:2px">${fmtFecha(resultado.cuotas[resultado.cuotas.length - 1]?.fechaVencimiento)}</p></div>
        ${garantia ? `<div><span style="font-size:10px;color:#94a3b8;text-transform:uppercase">Garantía</span><p style="font-weight:600;margin-top:2px">${garantia}</p></div>` : ''}
      </div>

      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:${color};border-bottom:1px solid ${colorLight};padding-bottom:6px;margin:0 0 10px">
        ${esCredito ? 'Tabla de amortización' : 'Calendario de cupones'}
      </div>
      <table>
        <thead><tr>
          <th style="text-align:center">N°</th>
          <th style="text-align:left">Vencimiento</th>
          <th>Pago</th>
          <th>Capital devuelto</th>
          <th>Cupón / Interés</th>
          <th>Saldo pendiente</th>
        </tr></thead>
        <tbody>${filas}</tbody>
        <tfoot><tr>
          <td colspan="2">TOTALES</td>
          <td>${fmtCLP(Math.round(resultado.totalPagar))}</td>
          <td>${fmtCLP(resultado.monto)}</td>
          <td>${fmtCLP(Math.round(resultado.totalIntereses))}</td>
          <td>—</td>
        </tr></tfoot>
      </table>

      ${notas ? `<div style="margin-top:20px;padding:12px 16px;background:#f8fafc;border-left:4px solid ${color};border-radius:4px;font-size:11px;color:#475569"><strong>Notas:</strong> ${notas}</div>` : ''}

      <div style="margin-top:48px;display:grid;grid-template-columns:1fr 1fr;gap:60px">
        <div style="border-top:1px solid #334155;padding-top:8px;font-size:11px;color:#64748b;text-align:center">${esc(empresa || 'Solicitante / Deudor')}<br/>${esc(rutEmpresa)}</div>
        <div style="border-top:1px solid #334155;padding-top:8px;font-size:11px;color:#64748b;text-align:center">${esc(prestamista || (esCredito ? 'Prestamista / Acreedor' : 'Inversionista / Acreedor'))}<br/>${esc(rutPrestamista)}</div>
      </div>
      <div style="margin-top:28px;padding-top:14px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8">
        <span>Generado el ${new Date().toLocaleDateString('es-CL')} · Propuesta sujeta a aprobación</span>
        <span>RestManager CL</span>
      </div>
    </body></html>`;
  };

  const imprimirPropuesta = () => {
    const html = buildPrintHtml();
    if (!html) return;
    const ventana = window.open('', '_blank', 'width=900,height=700');
    ventana.document.write(html);
    ventana.document.close();
    setTimeout(() => ventana.print(), 400);
  };

  /* ─── clases reutilizables ─── */
  const inp  = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white';
  const inpB = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white';
  const lbl  = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

  const esCredito = modo === 'credito';
  const accentRing = esCredito ? 'ring-violet-400' : 'ring-sky-400';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">

      {/* ── Header ── */}
      <header className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${esCredito ? 'bg-violet-600' : 'bg-sky-600'}`}>
              <Calculator size={18} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-base leading-none">Calculadora Financiera</h1>
              <p className="text-xs text-gray-400 mt-0.5">Créditos y Bonos</p>
            </div>
          </div>
          <a href="/menu" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-violet-600 transition-colors">
            <ArrowLeft size={14} /> Ver menú
          </a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* ── Selector de modo ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 flex gap-2">
          <button
            onClick={() => setModo('credito')}
            className={`flex-1 flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm transition-all ${
              esCredito
                ? 'bg-violet-600 text-white shadow-md shadow-violet-200'
                : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <CreditCard size={18} />
            <div className="text-left">
              <p className="leading-none">Modo Crédito</p>
              <p className={`text-xs font-normal mt-0.5 ${esCredito ? 'text-violet-200' : 'text-gray-400'}`}>Pagas cuotas con capital + interés</p>
            </div>
          </button>
          <button
            onClick={() => setModo('bono')}
            className={`flex-1 flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm transition-all ${
              !esCredito
                ? 'bg-sky-600 text-white shadow-md shadow-sky-200'
                : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <Landmark size={18} />
            <div className="text-left">
              <p className="leading-none">Modo Bono</p>
              <p className={`text-xs font-normal mt-0.5 ${!esCredito ? 'text-sky-200' : 'text-gray-400'}`}>Pagas cupones y devuelves capital al final</p>
            </div>
          </button>
        </div>

        {/* ── Info del modo ── */}
        {!esCredito && (
          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 text-sm text-sky-800">
            <p className="font-semibold mb-1">¿Qué es un Bono?</p>
            <p className="text-sky-700">Tú recibes el capital ahora y pagas solo intereses (cupones) de forma periódica. Al vencimiento devuelves el 100% del capital de una sola vez. Ideal para propuestas a inversionistas privados.</p>
          </div>
        )}

        {/* ── Parámetros ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black text-white ${esCredito ? 'bg-violet-600' : 'bg-sky-600'}`}>1</span>
            {esCredito ? 'Condiciones del crédito' : 'Condiciones del bono'}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Monto {esCredito ? 'solicitado' : 'del bono'}</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                <input type="text" inputMode="numeric" placeholder="Ej: 5000000"
                  className={`${esCredito ? inp : inpB} pl-8`}
                  value={monto} onChange={e => setMonto(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
              {monto && <p className={`text-xs mt-1 font-semibold ${esCredito ? 'text-violet-600' : 'text-sky-600'}`}>{fmtCLP(parseInt(monto) || 0)}</p>}
            </div>
            <div>
              <label className={lbl}>Plazo (meses)</label>
              <input type="number" min="1" max="600" placeholder="Ej: 24"
                className={esCredito ? inp : inpB}
                value={plazo} onChange={e => setPlazo(e.target.value)} />
              {plazo > 0 && <p className="text-xs text-gray-400 mt-1">{Math.floor(plazo/12) > 0 ? `${Math.floor(plazo/12)} año(s)${plazo%12>0?` y ${plazo%12} mes(es)`:''}` : `${plazo} mes(es)`}</p>}
            </div>
          </div>

          {/* ─ Tipo de interés (crédito) ─ */}
          {esCredito && (
            <>
              <div>
                <label className={lbl}>Tipo de interés</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { val: 'frances',      title: '% Tasa anual',          sub: 'Amortización francesa (banco)' },
                    { val: 'capital_fijo', title: '$ Interés fijo pactado', sub: 'Monto acordado en pesos' },
                  ].map(op => (
                    <button key={op.val} type="button" onClick={() => setTipoInteres(op.val)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${tipoInteres === op.val ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <p className="text-sm font-bold text-gray-800">{op.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{op.sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tipoInteres === 'frances' ? (
                  <div>
                    <label className={lbl}>Tasa de interés anual (%)</label>
                    <input type="number" min="0" max="200" step="any" placeholder="Ej: 18.00"
                      className={inp} value={tasaAnual} onChange={e => setTasaAnual(e.target.value)} />
                  </div>
                ) : (
                  <div>
                    <label className={lbl}>Interés total acordado (CLP)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                      <input type="text" inputMode="numeric" placeholder="Ej: 500000"
                        className={`${inp} pl-8`}
                        value={interesCapital} onChange={e => setInteresCapital(e.target.value.replace(/[^0-9]/g, ''))} />
                    </div>
                  </div>
                )}
                <div>
                  <label className={lbl}>Fecha de inicio</label>
                  <input type="date" className={inp} value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* ─ Tipo de interés (bono) ─ */}
          {!esCredito && (
            <>
              <div>
                <label className={lbl}>Tipo de cupón</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { val: 'tasa',       title: '% Tasa anual',          sub: 'Cupones calculados por tasa' },
                    { val: 'monto_fijo', title: '$ Interés total fijo',   sub: 'Monto acordado distribuido' },
                  ].map(op => (
                    <button key={op.val} type="button" onClick={() => setTipoBono(op.val)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${tipoBono === op.val ? 'border-sky-500 bg-sky-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <p className="text-sm font-bold text-gray-800">{op.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{op.sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tipoBono === 'tasa' ? (
                  <div>
                    <label className={lbl}>Tasa cupón anual (%)</label>
                    <input type="number" min="0" max="200" step="any" placeholder="Ej: 12.00"
                      className={inpB} value={tasaAnual} onChange={e => setTasaAnual(e.target.value)} />
                  </div>
                ) : (
                  <div>
                    <label className={lbl}>Interés total acordado (CLP)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                      <input type="text" inputMode="numeric" placeholder="Ej: 600000"
                        className={`${inpB} pl-8`}
                        value={bInteresTotal} onChange={e => setBInteresTotal(e.target.value.replace(/[^0-9]/g, ''))} />
                    </div>
                    {bInteresTotal && <p className="text-xs text-sky-600 mt-1 font-semibold">{fmtCLP(parseInt(bInteresTotal)||0)}</p>}
                  </div>
                )}
                <div>
                  <label className={lbl}>Frecuencia de cupones</label>
                  <select className={inpB} value={frecuencia} onChange={e => setFrecuencia(e.target.value)}>
                    {Object.entries(FRECUENCIAS_BONO).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Fecha de inicio</label>
                  <input type="date" className={inpB} value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
                </div>
                <div>
                  <label className={lbl}>Día fijo de pago (opcional)</label>
                  <input type="number" min="1" max="31" placeholder="Ej: 5"
                    className={inpB} value={diaPago} onChange={e => setDiaPago(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {esCredito && (
            <div>
              <label className={lbl}>Día fijo de pago (opcional)</label>
              <input type="number" min="1" max="31" placeholder="Ej: 5"
                className={inp} value={diaPago} onChange={e => setDiaPago(e.target.value)} />
            </div>
          )}
        </div>

        {/* ── Resultado ── */}
        {resultado && (
          <>
            {/* Tarjetas resumen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {esCredito ? [
                { label: 'Cuota mensual',    value: fmtCLP(Math.round(resultado.cuotaMensual)), from: 'from-violet-600', to: 'to-violet-800', sub: 'text-violet-200' },
                { label: 'Total intereses',  value: fmtCLP(Math.round(resultado.totalIntereses)), from: 'from-orange-500', to: 'to-orange-700', sub: 'text-orange-200' },
                { label: 'Total a devolver', value: fmtCLP(Math.round(resultado.totalPagar)),    from: 'from-slate-700',  to: 'to-slate-900',  sub: 'text-slate-400' },
              ] : [
                { label: `Cupón ${FRECUENCIAS_BONO[frecuencia].label.toLowerCase()}`, value: fmtCLP(Math.round(resultado.cupon)), from: 'from-sky-600',   to: 'to-sky-800',   sub: 'text-sky-200' },
                { label: 'Total intereses pagados', value: fmtCLP(Math.round(resultado.totalIntereses)), from: 'from-orange-500', to: 'to-orange-700', sub: 'text-orange-200' },
                { label: 'Pago final (cupón+capital)', value: fmtCLP(Math.round(resultado.pagoFinal)), from: 'from-slate-700', to: 'to-slate-900', sub: 'text-slate-400' },
              ].map(c => (
                <div key={c.label} className={`bg-gradient-to-br ${c.from} ${c.to} rounded-2xl p-5 text-white shadow-md`}>
                  <p className={`text-xs font-semibold ${c.sub} uppercase tracking-wide mb-1`}>{c.label}</p>
                  <p className="text-2xl font-black">{c.value}</p>
                </div>
              ))}
            </div>

            {/* Detalle */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                {(esCredito ? [
                  { label: 'Monto solicitado', value: fmtCLP(resultado.monto) },
                  { label: 'Plazo',            value: `${resultado.plazo} meses` },
                  { label: 'Primer pago',      value: fmtFecha(resultado.cuotas[0]?.fechaVencimiento) },
                  { label: 'Último pago',      value: fmtFecha(resultado.cuotas[resultado.cuotas.length-1]?.fechaVencimiento) },
                ] : [
                  { label: 'Valor del bono',       value: fmtCLP(resultado.monto) },
                  { label: 'Nº pagos de cupón',    value: `${resultado.numPeriodos} pagos` },
                  { label: 'Primer cupón',         value: fmtFecha(resultado.cuotas[0]?.fechaVencimiento) },
                  { label: 'Vencimiento (capital)', value: fmtFecha(resultado.cuotas[resultado.cuotas.length-1]?.fechaVencimiento) },
                ]).map(d => (
                  <div key={d.label}>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">{d.label}</p>
                    <p className="font-bold text-gray-800 mt-0.5">{d.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                {esCredito
                  ? <>💡 Por cada <strong>$100</strong> prestados pagas <strong className="text-orange-600">${resultado.monto > 0 ? ((resultado.totalIntereses/resultado.monto)*100).toFixed(1) : '0'}</strong> en intereses</>
                  : <>💡 El inversionista recibe <strong className="text-sky-600">{resultado.numPeriodos} cupones</strong> de {fmtCLP(Math.round(resultado.cupon))} y recupera el capital completo al vencimiento</>
                }
              </div>
            </div>

            {/* Tabla */}
            <div>
              <button onClick={() => setMostrarTabla(v => !v)}
                className={`flex items-center gap-2 text-sm font-semibold transition-colors mb-3 ${esCredito ? 'text-violet-700 hover:text-violet-900' : 'text-sky-700 hover:text-sky-900'}`}>
                {mostrarTabla ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                {mostrarTabla
                  ? (esCredito ? 'Ocultar tabla de amortización' : 'Ocultar calendario de cupones')
                  : (esCredito ? 'Ver tabla de amortización' : 'Ver calendario de cupones')}
              </button>

              {mostrarTabla && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`text-white text-xs ${esCredito ? 'bg-violet-600' : 'bg-sky-600'}`}>
                          <th className="px-4 py-3 text-center">N°</th>
                          <th className="px-4 py-3 text-left">Vencimiento</th>
                          <th className="px-4 py-3 text-right">Pago</th>
                          <th className="px-4 py-3 text-right">Capital</th>
                          <th className="px-4 py-3 text-right">Cupón/Interés</th>
                          <th className="px-4 py-3 text-right">Saldo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {resultado.cuotas.map((c, i) => {
                          const saldo = esCredito
                            ? resultado.monto - resultado.cuotas.slice(0,i+1).reduce((s,x)=>s+x.capital,0)
                            : c.saldo;
                          const esFinal = !esCredito && c.esFinal;
                          return (
                            <tr key={c.numero} className={`${i%2===0?'bg-white':'bg-gray-50/50'} ${esFinal ? 'bg-sky-50 font-semibold' : ''}`}>
                              <td className="px-4 py-2.5 text-center text-gray-400 font-semibold">{c.numero}</td>
                              <td className="px-4 py-2.5 text-gray-600">{fmtFecha(c.fechaVencimiento)}</td>
                              <td className="px-4 py-2.5 text-right font-semibold text-gray-800">{fmtCLP(c.monto)}</td>
                              <td className="px-4 py-2.5 text-right text-green-700">{fmtCLP(c.capital)}</td>
                              <td className="px-4 py-2.5 text-right text-orange-600">{fmtCLP(c.intereses)}</td>
                              <td className="px-4 py-2.5 text-right text-gray-400">{fmtCLP(Math.max(0,Math.round(saldo)))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                          <td colSpan={2} className="px-4 py-3 text-gray-700">TOTALES</td>
                          <td className="px-4 py-3 text-right">{fmtCLP(Math.round(resultado.totalPagar))}</td>
                          <td className="px-4 py-3 text-right text-green-700">{fmtCLP(resultado.monto)}</td>
                          <td className="px-4 py-3 text-right text-orange-600">{fmtCLP(Math.round(resultado.totalIntereses))}</td>
                          <td className="px-4 py-3 text-right text-gray-400">—</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Datos propuesta */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
              <button onClick={() => setMostrarForm(v => !v)} className="w-full flex items-center justify-between">
                <h2 className="font-bold text-gray-800 flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black text-white ${esCredito ? 'bg-violet-600' : 'bg-sky-600'}`}>2</span>
                  Datos para la propuesta <span className="text-xs font-normal text-gray-400">(opcional)</span>
                </h2>
                {mostrarForm ? <ChevronUp size={18} className="text-gray-400"/> : <ChevronDown size={18} className="text-gray-400"/>}
              </button>

              {mostrarForm && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {[
                    { lbl: 'Tu empresa (deudor)',                        val: empresa,        set: setEmpresa,        ph: 'Nombre de tu empresa' },
                    { lbl: 'RUT empresa',                                 val: rutEmpresa,     set: setRutEmpresa,     ph: '12.345.678-9' },
                    { lbl: esCredito ? 'Prestamista / Acreedor' : 'Inversionista / Acreedor', val: prestamista, set: setPrestamista, ph: 'Nombre del prestamista' },
                    { lbl: 'RUT prestamista',                             val: rutPrestamista, set: setRutPrestamista, ph: 'Opcional' },
                    { lbl: 'Propósito del crédito / bono',               val: proposito,      set: setProposito,      ph: 'Ej: Capital de trabajo, expansión...', col: 2 },
                    { lbl: 'Garantía ofrecida',                           val: garantia,       set: setGarantia,       ph: 'Ej: Vehículo, maquinaria...' },
                    { lbl: 'Notas adicionales',                           val: notas,          set: setNotas,          ph: 'Condiciones especiales, observaciones...' },
                  ].map(f => (
                    <div key={f.lbl} className={f.col === 2 ? 'sm:col-span-2' : ''}>
                      <label className={lbl}>{f.lbl}</label>
                      <input type="text" placeholder={f.ph}
                        className={esCredito ? inp : inpB}
                        value={f.val} onChange={e => f.set(e.target.value)} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botón imprimir */}
            <button onClick={imprimirPropuesta}
              className={`w-full flex items-center justify-center gap-3 text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-lg text-base ${
                esCredito
                  ? 'bg-violet-600 hover:bg-violet-700 shadow-violet-200'
                  : 'bg-sky-600 hover:bg-sky-700 shadow-sky-200'
              }`}>
              <Printer size={20}/>
              Generar e Imprimir Propuesta de {esCredito ? 'Crédito' : 'Bono'}
            </button>
          </>
        )}

        {!resultado && (
          <div className="text-center py-16 text-gray-400">
            <Calculator size={52} className="mx-auto mb-4 text-gray-200"/>
            <p className="text-lg font-semibold text-gray-500">Ingresa el monto y plazo para comenzar</p>
            <p className="text-sm mt-1">El resultado se actualiza automáticamente</p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pb-4">
          Esta calculadora es solo de referencia. Los valores reales pueden variar según las condiciones acordadas.
        </p>
      </div>
    </div>
  );
}
